import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import { generatePath } from '@/services/router/generate-path';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { importStepHref, type ImportWizardStep } from '@/features/imports';
import { useImportId } from './[importId]/use-import-id';

const LOADING_LABEL = 'Loading…';

type ImportsRouteViewProps = {
  children: ReactNode;
};

/**
 * Any `/imports` screen: the list, the upload screen, a wizard step, or a finished import's own
 * page.
 *
 * One component for all of them because the only thing they ever needed separately was the
 * `journeys` URL to hand back to while `csv-imports-migration` was off. That flag reached 100% on
 * 2026-09-14 and is gone, so there is no hand-back and nothing left that varies per screen.
 *
 * The permission gate is here rather than on each screen because that is where the embedded app
 * puts it: `app/pods/imports/route.ts` gates the whole `/imports` tree on `journeys/imports/view`
 * from the parent route, and every screen under it inherits that. Creating and editing are
 * separate permissions that the screens themselves answer, so a viewer who may not create still
 * reaches them and sees them read-only.
 *
 * The loader waits on the resolved workspace rather than the URL segment: a page's reads go
 * through `requireWorkspaceId`, which throws while the workspaces list is still loading, and the
 * `/workspaces/last` sentinel leaves route params stale until it settles.
 */
export function ImportsRouteView({ children }: ImportsRouteViewProps) {
  const workspace = useCurrentWorkspace();

  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return (
    <ProtectedRoute
      permission="journeys/imports/view"
      redirectTo={generatePath('/workspaces/:workspaceId/journeys', { workspaceId: workspace.id })}
    >
      {children}
    </ProtectedRoute>
  );
}

type JourneysImportForwardProps = {
  /** The migrated URL this one forwards to; `null` while the workspace resolves. */
  migratedHref: string | null;
};

/**
 * A legacy `journeys` import URL, which this app claims only to forward.
 *
 * These are kept rather than dropped because the links pointing at them are not all ours: the
 * embedded app's nav and its own templates still use the `journeys` spelling, and they convert in
 * the FPS-2702 teardown. Until then, dropping the claim would hand the URL to an Ember route that
 * the teardown is about to delete.
 *
 * Forwarding replaces the history entry, mirroring an Ember route-hook redirect, so Back does not
 * bounce off the old URL.
 */
function JourneysImportForward({ migratedHref }: JourneysImportForwardProps) {
  if (migratedHref == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={migratedHref} options={{ shouldReplaceHistoryEntry: true }} />;
}

/** The legacy `/journeys/imports` URL — the index only; each screen below has its own twin. */
export function JourneysImportsHandoff() {
  const router = useRouter();
  const migratedHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: '/workspaces/$workspaceId/imports', params: { workspaceId } }).href,
  );

  return <JourneysImportForward migratedHref={migratedHref} />;
}

/**
 * The legacy `/journeys/imports/new` URL, the twin of the migrated upload screen.
 *
 * Reached from the segment and object pages, which pass the type and ids in the query string —
 * `useMigrationHandoffHref` carries those across, so a seeded link keeps its seeds.
 */
export function JourneysNewImportHandoff() {
  const router = useRouter();
  const migratedHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: '/workspaces/$workspaceId/imports/new', params: { workspaceId } }).href,
  );

  return <JourneysImportForward migratedHref={migratedHref} />;
}

type JourneysWizardStepHandoffProps = {
  /** Which step this URL is, so the forward knows its migrated address. */
  step: ImportWizardStep;
};

/**
 * The legacy `/journeys/imports/:importId` and `/journeys/imports/:importId/setup/<step>` URLs,
 * twins of the migrated screens.
 */
export function JourneysWizardStepHandoff({ step }: JourneysWizardStepHandoffProps) {
  const importId = useImportId();
  const migratedHref = useMigrationHandoffHref((workspaceId) => importStepHref(Number(workspaceId), importId, step));

  return <JourneysImportForward migratedHref={migratedHref} />;
}
